// AIRG — The Decision Border
// AI Runtime Governor · product hero. Full mechanism: tool-call boundary + SURGE ledger.
// Path: src/components/ui/AirgDecisionBorder.tsx  ·  No external deps.
// Static scene = declarative JSX; per-frame motion = imperative via refs; React state = ledger only.

import { useEffect, useRef, useState } from "react";

/* ── types ── */
type Verdict = "allow" | "review" | "block";
interface ActionDef { op: string; sys: string; agent: string; verdict: Verdict; note: string; cost: string; }
interface SystemDef { id: string; label: string; y: number; }
interface AgentDef { id: string; label: string; fp: string; y: number; }
interface LedgerRow { id: string; op: string; verdict: Verdict; prev: string; cost: string; }
interface Tally { allow: number; review: number; block: number; cost: number; }

/* ── palette (cold instrument grade) ── */
const C = {
  steel: "#6f9fb5", dim: "#657483", dim2: "#3d4854", txt: "#c2ccd6", bright: "#eef3f7",
  allow: "#3fb98a", review: "#d99a3f", block: "#d1495b", seal: "#c9a24a", cost: "#9a8a55",
  line: "rgba(140,170,200,.16)",
} as const;
const VC: Record<Verdict, string> = { allow: C.allow, review: C.review, block: C.block };

/* ── geometry ── */
const BORDER_X = 570, SYS_X = 150, AGT_X = 990;
const LANES = [120, 220, 320, 420, 520] as const;

const SYSTEMS: SystemDef[] = [
  { id: "payments", label: "Payments Rail",   y: LANES[0] },
  { id: "customer", label: "Customer Data",   y: LANES[1] },
  { id: "infra",    label: "Infrastructure",  y: LANES[2] },
  { id: "health",   label: "Healthcare",      y: LANES[3] },
  { id: "legal",    label: "Legal Workflows", y: LANES[4] },
];
const AGENTS: AgentDef[] = [
  { id: "billing", label: "billing-agent", fp: "9c1a", y: LANES[0] },
  { id: "data",    label: "data-agent",    fp: "4f7e", y: LANES[1] },
  { id: "ops",     label: "ops-copilot",   fp: "b2d5", y: LANES[2] },
  { id: "care",    label: "care-assist",   fp: "7a30", y: LANES[3] },
  { id: "legaldr", label: "legal-drafter", fp: "c81b", y: LANES[4] },
];
// Deterministic policy: same op → same verdict. `cost` = per-call governance cost attributed to the agent.
// ⚠ Placeholders — swap in AIRG's real op names, verdicts, and cost figures before this ships to /products/airg.
const ACTIONS: ActionDef[] = [
  { op: "payments.transfer",   sys: "payments", agent: "billing", verdict: "review", note: "amount over threshold", cost: "0.0043" },
  { op: "payments.refund",     sys: "payments", agent: "billing", verdict: "allow",  note: "within limit",          cost: "0.0018" },
  { op: "customer.read_pii",   sys: "customer", agent: "data",    verdict: "allow",  note: "scoped · logged",       cost: "0.0009" },
  { op: "customer.export_all", sys: "customer", agent: "ops",     verdict: "block",  note: "bulk PII export",       cost: "0.0071" },
  { op: "infra.scale_cluster", sys: "infra",    agent: "ops",     verdict: "allow",  note: "non-destructive",       cost: "0.0026" },
  { op: "infra.delete_volume", sys: "infra",    agent: "ops",     verdict: "block",  note: "destructive · prod",    cost: "0.0038" },
  { op: "ehr.read_record",     sys: "health",   agent: "care",    verdict: "allow",  note: "consent on file",       cost: "0.0012" },
  { op: "ehr.update_dosage",   sys: "health",   agent: "care",    verdict: "review", note: "clinical safety hold",  cost: "0.0033" },
  { op: "contract.sign",       sys: "legal",    agent: "legaldr", verdict: "review", note: "value over £50k",       cost: "0.0057" },
  { op: "legal.disclose_docs", sys: "legal",    agent: "legaldr", verdict: "block",  note: "privileged material",   cost: "0.0064" },
];
const sysById: Record<string, SystemDef> = Object.fromEntries(SYSTEMS.map((s) => [s.id, s]));
const agtById: Record<string, AgentDef> = Object.fromEntries(AGENTS.map((a) => [a.id, a]));

const GRID: { x: number; y: number }[] = [];
for (let x = 40; x <= 1100; x += 32) for (let y = 56; y <= 548; y += 32) GRID.push({ x, y });
const TICKS: number[] = [];
for (let y = 70; y <= 540; y += 16) TICKS.push(y);

const hex = (n: number): string =>
  Array.from({ length: n }, () => "0123456789abcdef"[(Math.random() * 16) | 0]).join("");

// Resting state: the ledger and the boundary already show one sealed decision,
// so the panel reads as a governed system before any motion starts — and for
// anyone who never sees the motion at all.
const SEED_ROWS: LedgerRow[] = [
  { id: "2c95af60", op: "infra.delete_volume", verdict: "block", prev: "b41e", cost: "0.0038" },
  { id: "b41e08d7", op: "ehr.update_dosage", verdict: "review", prev: "7f3a", cost: "0.0033" },
  { id: "7f3a9c21", op: "payments.refund", verdict: "allow", prev: "gene", cost: "0.0018" },
];
const SEED_TALLY: Tally = { allow: 1, review: 1, block: 1, cost: 0.0089 };
const RESTING = { op: "payments.refund", y: LANES[0], note: "receipt sealed" } as const;

/* ── static node (declarative) ── */
interface NodeProps { x: number; y: number; label: string; sub: string; side: "left" | "right"; id: string; ns: "sys" | "agt"; }
function Node({ x, y, label, sub, side, id, ns }: NodeProps) {
  const w = 168, h = 44, left = side === "left" ? x : x - w;
  const pipCx = side === "left" ? w - 14 : 14;
  const cx = side === "left" ? w : 0;
  const stub = side === "left" ? w + 24 : -24;
  return (
    <g transform={`translate(${left},${y - h / 2})`}>
      <rect x={0} y={0} width={w} height={h} rx={8} fill="rgba(255,255,255,.018)" stroke={C.line} strokeWidth={1} />
      <circle data-pip={`${ns}-${id}`} cx={pipCx} cy={14} r={2.4} fill={C.dim} />
      <text x={16} y={19} fill={C.txt} fontFamily="var(--sans)" fontSize={12.5} fontWeight={600}>{label}</text>
      <text x={16} y={34} fill={C.dim} fontFamily="var(--mono)" fontSize={9.5} letterSpacing={1}>{sub}</text>
      <line x1={cx} y1={h / 2} x2={stub} y2={h / 2} stroke={C.line} strokeWidth={1} />
    </g>
  );
}

export default function AirgDecisionBorder() {
  const svgRef = useRef<SVGSVGElement>(null);
  const packetsRef = useRef<SVGGElement>(null);
  const routesRef = useRef<SVGGElement>(null);
  const scanRef = useRef<SVGRectElement>(null);
  const prevHash = useRef<string>(SEED_ROWS[0].id);

  const [rows, setRows] = useState<LedgerRow[]>(SEED_ROWS);
  const [tally, setTally] = useState<Tally>(SEED_TALLY);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    const state = { alive: true, raf: 0 };
    const SVG_NS = "http://www.w3.org/2000/svg";

    const ease = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

    const el = <K extends keyof SVGElementTagNameMap>(
      tag: K, attrs: Record<string, string | number>, parent?: Element,
    ): SVGElementTagNameMap[K] => {
      const n = document.createElementNS(SVG_NS, tag);
      for (const k in attrs) n.setAttribute(k, String(attrs[k]));
      if (parent) parent.appendChild(n);
      return n;
    };
    const pip = (ns: "sys" | "agt", id: string): Element | null =>
      svgRef.current?.querySelector(`[data-pip="${ns}-${id}"]`) ?? null;

    const tween = (ms: number, cb: (t: number, p: number) => void): Promise<void> =>
      new Promise((res) => {
        if (reduce || !state.alive) { cb(1, 1); return res(); }
        const t0 = performance.now();
        const f = (now: number) => {
          if (!state.alive) return res();
          const p = Math.min(1, (now - t0) / ms);
          cb(ease(p), p);
          p < 1 ? requestAnimationFrame(f) : res();
        };
        requestAnimationFrame(f);
      });
    const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, reduce ? ms * 0.2 : ms));

    function seal(a: ActionDef, verdict: Verdict): void {
      const h = hex(8), prev = prevHash.current.slice(0, 4);
      prevHash.current = h;
      setTally((t) => ({ ...t, [verdict]: t[verdict] + 1, cost: t.cost + parseFloat(a.cost) }));
      setRows((r) => [{ id: h, op: a.op, verdict, prev, cost: a.cost }, ...r].slice(0, 5));
    }

    function makePacket(op: string) {
      const w = Math.max(72, op.length * 7.4 + 22);
      const g = el("g", { opacity: 0 }, packetsRef.current ?? undefined);
      const box = el("rect", { x: -w / 2, y: -11, width: w, height: 22, rx: 6, fill: "#0c131b", stroke: C.steel, "stroke-width": 1 }, g);
      const dot = el("circle", { cx: -w / 2 + 11, cy: 0, r: 2.6, fill: C.steel }, g);
      const t = el("text", { x: 5, y: 4, fill: C.txt, "font-family": "var(--mono)", "font-size": 11.5, "text-anchor": "middle" }, g);
      t.textContent = op;
      return { g, box, pip: dot, place: (x: number, y: number) => g.setAttribute("transform", `translate(${x},${y})`) };
    }
    function trailStart() {
      const pl = el("polyline", { points: "", fill: "none", stroke: C.steel, "stroke-width": 1.4, "stroke-linecap": "round", "stroke-linejoin": "round", opacity: 0.32, filter: "url(#airg-soft)" }, routesRef.current ?? undefined);
      const pts: string[] = [];
      return {
        push: (x: number, y: number) => { pts.push(x + "," + y); if (pts.length > 20) pts.shift(); pl.setAttribute("points", pts.join(" ")); },
        color: (c: string) => { pl.setAttribute("stroke", c); pl.setAttribute("opacity", "0.5"); },
        fade: () => {
          let o = parseFloat(pl.getAttribute("opacity") ?? "0.5");
          const id = window.setInterval(() => { o -= 0.04; pl.setAttribute("opacity", String(Math.max(0, o))); if (o <= 0) { window.clearInterval(id); pl.remove(); } }, 32);
        },
      };
    }
    function evalRig(y: number) {
      const bx = BORDER_X, g = el("g", {}, packetsRef.current ?? undefined), segs: SVGRectElement[] = [];
      for (let i = 0; i < 6; i++) segs.push(el("rect", { x: bx + 16, y: y - 15 + i * 5.2, width: 14, height: 3.4, rx: 1, fill: "rgba(140,170,200,.14)" }, g));
      return {
        g,
        run: async () => { for (let i = 0; i < 6; i++) { segs[i].setAttribute("fill", C.steel); await wait(70); } },
        verdict: (v: Verdict) => segs.forEach((s) => s.setAttribute("fill", VC[v])),
        badge: (v: Verdict, label: string): SVGGElement => {
          const bg = el("g", { transform: `translate(${bx},${y})`, opacity: 0 }, g);
          const w = v === "review" ? 150 : 118;
          el("rect", { x: -w / 2, y: 20, width: w, height: 34, rx: 7, fill: "#0c131b", stroke: VC[v], "stroke-width": 1.2 }, bg);
          const vt = el("text", { x: 0, y: 34, fill: VC[v], "font-family": "var(--mono)", "font-size": 11.5, "letter-spacing": 2, "text-anchor": "middle", "font-weight": 700 }, bg);
          vt.textContent = v.toUpperCase();
          const nt = el("text", { x: 0, y: 47, fill: C.dim, "font-family": "var(--mono)", "font-size": 8.5, "text-anchor": "middle" }, bg);
          nt.textContent = label;
          void tween(260, (t) => bg.setAttribute("opacity", String(t)));
          return bg;
        },
        remove: () => g.remove(),
      };
    }

    let queue: ActionDef[] = [...ACTIONS].sort(() => Math.random() - 0.5), qi = 0;
    const nextAction = (): ActionDef => { if (qi >= queue.length) { queue = [...ACTIONS].sort(() => Math.random() - 0.5); qi = 0; } return queue[qi++]; };

    async function runCycle(): Promise<void> {
      setStarted(true);
      const a = nextAction(), sys = sysById[a.sys], agt = agtById[a.agent];
      const ax = AGT_X - 168, ay = agt.y, ex = BORDER_X - 14, ey = sys.y, sx = SYS_X + 24, sy = sys.y;

      pip("agt", agt.id)?.setAttribute("fill", C.steel);
      const p = makePacket(a.op), tr = trailStart();

      p.place(ax, ay);
      await tween(240, (t) => p.g.setAttribute("opacity", String(t)));
      await tween(1350, (t) => { const x = lerp(ax, ex, t), y = lerp(ay, ey, t); p.place(x, y); tr.push(x, y); });

      const rig = evalRig(ey);
      await rig.run();
      rig.verdict(a.verdict);
      p.box.setAttribute("stroke", VC[a.verdict]);
      p.pip.setAttribute("fill", VC[a.verdict]);
      tr.color(VC[a.verdict]);

      const label = a.verdict === "review" ? "HUMAN IN THE LOOP" : a.verdict === "block" ? a.note : "receipt sealed";
      const badge = rig.badge(a.verdict, label);
      await wait(120);

      if (a.verdict === "allow") {
        await tween(1000, (t) => { const x = lerp(ex, sx, t), y = lerp(ey, sy, t); p.place(x, y); tr.push(x, y); });
        const sp = pip("sys", sys.id); sp?.setAttribute("fill", C.allow);
        seal(a, "allow");
        await tween(360, (t) => p.g.setAttribute("opacity", String(1 - t)));
        await wait(500); sp?.setAttribute("fill", C.dim);
      } else if (a.verdict === "review") {
        seal(a, "review");
        await tween(1300, (t) => p.g.setAttribute("opacity", String(0.55 + 0.45 * Math.abs(Math.sin(t * Math.PI * 3)))));
        await tween(340, (t) => p.g.setAttribute("opacity", String(1 - t)));
      } else {
        seal(a, "block");
        await tween(300, (t) => p.place(ex - 26 * Math.sin(t * Math.PI), ey));
        await tween(420, (t) => p.g.setAttribute("opacity", String(1 - t)));
      }

      pip("agt", agt.id)?.setAttribute("fill", C.dim);
      tr.fade();
      await tween(320, (t) => badge.setAttribute("opacity", String(1 - t)));
      rig.remove();
      p.g.remove();
    }

    if (!reduce && scanRef.current) {
      const top = 70, bot = 540 - 64; let t = 0;
      const loop = () => {
        if (!state.alive || !scanRef.current) return;
        t += 0.0016;
        scanRef.current.setAttribute("y", (top + (bot - top) * (0.5 + 0.5 * Math.sin(t * Math.PI * 2))).toFixed(1));
        state.raf = requestAnimationFrame(loop);
      };
      state.raf = requestAnimationFrame(loop);
    }

    void (async function loop() {
      await wait(700);
      while (state.alive) {
        await runCycle();
        await wait(reduce ? 400 : 2600 + Math.random() * 1600);
      }
    })();

    return () => {
      state.alive = false;
      cancelAnimationFrame(state.raf);
      packetsRef.current?.replaceChildren();
      routesRef.current?.replaceChildren();
    };
  }, []);

  return (
    <div className="airg-root">
      <style>{`
        .airg-root{
          --mono:ui-monospace,"SF Mono","JetBrains Mono",Menlo,Consolas,monospace;
          --sans:"Inter",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
          max-width:1140px;margin:0 auto;color:${C.txt};font-family:var(--sans);-webkit-font-smoothing:antialiased;
        }
        .airg-root *{box-sizing:border-box}
        .airg-card{position:relative;border:1px solid rgba(140,170,200,.18);border-radius:14px;overflow:hidden;
          background:radial-gradient(120% 90% at 50% -10%, rgba(111,159,181,.06), transparent 55%),linear-gradient(180deg,#0a0f16,#070a0f);
          box-shadow:0 30px 80px -40px rgba(0,0,0,.9),inset 0 1px 0 rgba(255,255,255,.03);}
        .airg-card::before,.airg-card::after{content:"";position:absolute;width:14px;height:14px;pointer-events:none;border:1px solid rgba(140,170,200,.18)}
        .airg-card::before{top:10px;left:10px;border-right:0;border-bottom:0}
        .airg-card::after{bottom:10px;right:10px;border-left:0;border-top:0}
        .airg-head{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px 22px 14px;border-bottom:1px solid rgba(140,170,200,.10)}
        .airg-kicker{font-family:var(--mono);font-size:10.5px;letter-spacing:.32em;color:${C.dim};text-transform:uppercase;display:flex;align-items:center;gap:9px}
        .airg-dot{width:6px;height:6px;border-radius:50%;background:${C.allow};animation:airg-live 2.6s ease-out infinite}
        @keyframes airg-live{0%{box-shadow:0 0 0 0 rgba(63,185,138,.45)}70%,100%{box-shadow:0 0 0 7px rgba(63,185,138,0)}}
        .airg-illus{border:1px solid rgba(140,170,200,.22);border-radius:4px;padding:2px 7px;letter-spacing:.18em;color:${C.dim2}}
        .airg-title{font-size:19px;font-weight:600;letter-spacing:-.01em;color:${C.bright};margin-top:6px}
        .airg-title b{color:${C.steel};font-weight:600}
        .airg-legend{display:flex;gap:18px;align-items:center;flex-wrap:wrap}
        .airg-leg{display:flex;align-items:center;gap:7px;font-family:var(--mono);font-size:10.5px;letter-spacing:.14em;text-transform:uppercase}
        .airg-leg i{width:8px;height:8px;border-radius:2px;display:inline-block}
        .airg-svg{display:block;width:100%;height:auto}
        .airg-ledger{border-top:1px solid rgba(140,170,200,.10);padding:11px 20px 13px;display:flex;align-items:center;gap:18px;min-height:52px;background:linear-gradient(180deg,rgba(255,255,255,.012),transparent)}
        .airg-ltag{font-family:var(--mono);font-size:9.5px;letter-spacing:.22em;color:${C.dim2};text-transform:uppercase;white-space:nowrap;line-height:1.5}
        .airg-ltag b{color:${C.seal};font-weight:500}
        .airg-rows{display:flex;gap:10px;overflow:hidden;flex:1;min-width:0;-webkit-mask-image:linear-gradient(90deg,transparent,#000 22px,#000 92%,transparent);mask-image:linear-gradient(90deg,transparent,#000 22px,#000 92%,transparent)}
        .airg-row{display:flex;align-items:center;gap:9px;flex:0 0 auto;font-family:var(--mono);font-size:11px;padding:5px 10px;border-radius:6px;border:1px solid rgba(140,170,200,.10);background:rgba(255,255,255,.015);white-space:nowrap;animation:airg-in .5s cubic-bezier(.2,.7,.2,1) both}
        @keyframes airg-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
        .airg-row .seal{width:8px;height:8px;border-radius:50%;background:${C.seal};box-shadow:0 0 8px rgba(201,162,74,.6)}
        .airg-row .hash{color:${C.txt}} .airg-row .op{color:${C.dim}} .airg-row .prev{color:${C.dim2};font-size:10px}
        .airg-row .cost{color:${C.cost};font-size:10px}
        .airg-row .vd{font-size:9.5px;letter-spacing:.12em;padding:1px 6px;border-radius:4px;border:1px solid currentColor}
        .airg-row.allow .vd{color:${C.allow}} .airg-row.review .vd{color:${C.review}} .airg-row.block .vd{color:${C.block}}
        .airg-tally{display:flex;gap:14px;font-family:var(--mono);font-size:10.5px;white-space:nowrap;color:${C.dim}}
        .airg-tally .ta b{color:${C.allow}} .airg-tally .tr b{color:${C.review}} .airg-tally .tb b{color:${C.block}} .airg-tally .tc b{color:${C.cost}}
        @media(max-width:720px){.airg-legend,.airg-tally,.airg-ltag{display:none}.airg-title{font-size:16px}}
        @media(prefers-reduced-motion:reduce){.airg-dot{animation:none}}
      `}</style>

      <div className="airg-card">
        <div className="airg-head">
          <div>
            <div className="airg-kicker">
              <span className="airg-dot" />AIRG · AI RUNTIME GOVERNOR
              <span className="airg-illus">Illustrative</span>
            </div>
            <div className="airg-title">The Decision <b>Border</b></div>
          </div>
          <div className="airg-legend">
            <span className="airg-leg" style={{ color: C.allow }}><i style={{ background: C.allow }} />Allow</span>
            <span className="airg-leg" style={{ color: C.review }}><i style={{ background: C.review }} />Review</span>
            <span className="airg-leg" style={{ color: C.block }}><i style={{ background: C.block }} />Block</span>
          </div>
        </div>

        <svg ref={svgRef} className="airg-svg" viewBox="0 0 1140 560" xmlns="http://www.w3.org/2000/svg"
             role="img" aria-label="AIRG governing agent tool-calls at the boundary: each request is judged ALLOW, REVIEW, or BLOCK and leaves a signed SURGE receipt.">
          <defs>
            <linearGradient id="airg-core" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#0d151d" /><stop offset="1" stopColor="#080d13" />
            </linearGradient>
            <linearGradient id="airg-scan" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="rgba(111,159,181,0)" />
              <stop offset=".5" stopColor="rgba(111,159,181,.55)" />
              <stop offset="1" stopColor="rgba(111,159,181,0)" />
            </linearGradient>
            <filter id="airg-soft" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="2.2" /></filter>
          </defs>

          <g>{GRID.map((d, i) => <circle key={i} cx={d.x} cy={d.y} r={0.7} fill="rgba(140,170,200,.05)" />)}</g>

          <text x={70} y={34} fill={C.dim} fontFamily="var(--mono)" fontSize={10.5} letterSpacing={3}>REAL SYSTEMS</text>
          <text x={1070} y={34} textAnchor="end" fill={C.dim} fontFamily="var(--mono)" fontSize={10.5} letterSpacing={3}>AI AGENTS</text>

          <g ref={routesRef} />

          <g>{SYSTEMS.map((s) => <Node key={s.id} ns="sys" id={s.id} x={SYS_X} y={s.y} label={s.label} sub={`${s.id}://prod`} side="left" />)}</g>
          <g>{AGENTS.map((a) => <Node key={a.id} ns="agt" id={a.id} x={AGT_X} y={a.y} label={a.label} sub={`fp:${a.fp}`} side="right" />)}</g>

          {/* AIRG boundary / runtime governor */}
          <g>
            <line x1={BORDER_X - 9} y1={70} x2={BORDER_X - 9} y2={540} stroke={C.line} strokeWidth={1} />
            <line x1={BORDER_X + 9} y1={70} x2={BORDER_X + 9} y2={540} stroke={C.line} strokeWidth={1} />
            <rect x={BORDER_X - 9} y={70} width={18} height={470} fill="url(#airg-core)" />
            {TICKS.map((y, i) => (
              <g key={i}>
                <line x1={BORDER_X - 13} y1={y} x2={BORDER_X - 9} y2={y} stroke="rgba(140,170,200,.14)" strokeWidth={1} />
                <line x1={BORDER_X + 9} y1={y} x2={BORDER_X + 13} y2={y} stroke="rgba(140,170,200,.14)" strokeWidth={1} />
              </g>
            ))}
            <text x={BORDER_X} y={305} fill="rgba(111,159,181,.5)" fontFamily="var(--mono)" fontSize={11}
                  letterSpacing={6} textAnchor="middle" transform={`rotate(-90 ${BORDER_X} 305)`}>AIRG · RUNTIME GOVERNOR</text>
            <text x={BORDER_X} y={60} fill={C.dim} fontFamily="var(--mono)" fontSize={9} letterSpacing={2.5} textAnchor="middle">TOOL-CALL BOUNDARY</text>
            <text x={BORDER_X} y={554} fill={C.dim2} fontFamily="var(--mono)" fontSize={8.5} letterSpacing={2} textAnchor="middle">PROPRIETARY · 6-LAYER · SURGE-SIGNED</text>
            <rect ref={scanRef} x={BORDER_X - 9} y={70} width={18} height={64} fill="url(#airg-scan)" opacity={0.5} />
          </g>

          {!started && (
            <g>
              <line
                x1={AGT_X - 168} y1={RESTING.y} x2={BORDER_X - 14} y2={RESTING.y}
                stroke={C.line} strokeWidth={1.2} strokeDasharray="3 5"
              />
              <g transform={`translate(${BORDER_X - 14},${RESTING.y})`}>
                <rect x={-70} y={-11} width={140} height={22} rx={6} fill="#0c131b" stroke={C.allow} strokeWidth={1} />
                <circle cx={-59} cy={0} r={2.6} fill={C.allow} />
                <text x={5} y={4} fill={C.txt} fontFamily="var(--mono)" fontSize={11.5} textAnchor="middle">
                  {RESTING.op}
                </text>
              </g>
              <g transform={`translate(${BORDER_X},${RESTING.y})`}>
                <rect x={-59} y={20} width={118} height={34} rx={7} fill="#0c131b" stroke={C.allow} strokeWidth={1.2} />
                <text x={0} y={34} fill={C.allow} fontFamily="var(--mono)" fontSize={11.5} letterSpacing={2} textAnchor="middle" fontWeight={700}>
                  ALLOW
                </text>
                <text x={0} y={47} fill={C.dim} fontFamily="var(--mono)" fontSize={8.5} textAnchor="middle">
                  {RESTING.note}
                </text>
              </g>
            </g>
          )}

          <g ref={packetsRef} />
        </svg>

        <div className="airg-ledger">
          <div className="airg-ltag">SURGE v2 LEDGER<br /><b>SHA-256 · Ed25519 · chained</b></div>
          <div className="airg-rows">
            {rows.map((r) => (
              <div key={r.id} className={`airg-row ${r.verdict}`}>
                <span className="seal" />
                <span className="hash">SRG-{r.id}</span>
                <span className="op">{r.op}</span>
                <span className="vd">{r.verdict.toUpperCase()}</span>
                <span className="cost">£{r.cost}</span>
                <span className="prev">↳ {r.prev}</span>
              </div>
            ))}
          </div>
          <div className="airg-tally">
            <span className="ta">ALLOW <b>{tally.allow}</b></span>
            <span className="tr">REVIEW <b>{tally.review}</b></span>
            <span className="tb">BLOCK <b>{tally.block}</b></span>
            <span className="tc">COST <b>£{tally.cost.toFixed(4)}</b></span>
          </div>
        </div>
      </div>
    </div>
  );
}
